import { useState, useEffect, useRef } from "react";
import { PlayIcon, PauseIcon, StopIcon, TrashIcon } from "@heroicons/react/24/outline";

// Custom Audio Player Component - Simple & Professional
const CustomAudioPlayer = ({ audioUrl, audioId, duration, onStop, onDelete, isUploading }) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [audioDuration, setAudioDuration] = useState(duration || 0)
  const audioRef = useRef(null)
  const progressRef = useRef(null)

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const updateTime = () => setCurrentTime(audio.currentTime)
    const updateDuration = () => setAudioDuration(audio.duration)
    const handleEnded = () => {
      setIsPlaying(false)
      setCurrentTime(0)
    }
    const handleError = (e) => {
      console.error('Audio error:', e)
      setIsPlaying(false)
    }

    audio.addEventListener('timeupdate', updateTime)
    audio.addEventListener('loadedmetadata', updateDuration)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('error', handleError)

    return () => {
      audio.removeEventListener('timeupdate', updateTime)
      audio.removeEventListener('loadedmetadata', updateDuration)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('error', handleError)
    }
  }, [])

  const togglePlay = () => {
    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
    } else {
      audio.play().then(() => {
        setIsPlaying(true)
      }).catch(err => {
        console.error('Play error:', err)
        setIsPlaying(false)
      })
    }
  }

  const handleStop = () => {
    const audio = audioRef.current
    if (!audio) return

    audio.pause()
    audio.currentTime = 0
    setCurrentTime(0)
    setIsPlaying(false)
    if (onStop) onStop()
  }

  const handleProgressClick = (e) => {
    const audio = audioRef.current
    if (!audio || !progressRef.current) return

    const rect = progressRef.current.getBoundingClientRect()
    const pos = (e.clientX - rect.left) / rect.width
    audio.currentTime = pos * audio.duration
  }

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const progress = audioDuration > 0 ? (currentTime / audioDuration) * 100 : 0

  return (
    <div className="w-full bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-2.5">
      <audio ref={audioRef} src={audioUrl} preload="auto" />
      
      <div className="flex items-center gap-2.5">
        {/* Play/Pause Button */}
        <button
          type="button"
          onClick={togglePlay}
          disabled={isUploading}
          className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-rose-600 hover:bg-rose-700 dark:bg-rose-500 dark:hover:bg-rose-600 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          title={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? (
            <PauseIcon className="w-4 h-4" />
          ) : (
            <PlayIcon className="w-4 h-4 ml-0.5" />
          )}
        </button>

        {/* Progress and Time */}
        <div className="flex-1 flex items-center gap-2">
          {/* Current Time */}
          <span className="text-xs text-gray-600 dark:text-gray-400 font-medium tabular-nums min-w-[35px]">
            {formatTime(currentTime)}
          </span>

          {/* Progress Bar */}
          <div 
            ref={progressRef}
            className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full cursor-pointer relative group"
            onClick={handleProgressClick}
          >
            <div 
              className="h-full bg-rose-500 dark:bg-rose-400 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
            <div 
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-rose-600 dark:bg-rose-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
              style={{ left: `calc(${progress}% - 6px)` }}
            />
          </div>

          {/* Total Duration */}
          <span className="text-xs text-gray-600 dark:text-gray-400 font-medium tabular-nums min-w-[35px]">
            {formatTime(audioDuration)}
          </span>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-1">
          {/* Stop Button */}
          <button
            type="button"
            onClick={handleStop}
            disabled={isUploading}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Stop"
          >
            <StopIcon className="w-4 h-4" />
          </button>

          {/* Delete Button */}
          <button
            type="button"
            onClick={onDelete}
            disabled={isUploading}
            className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Delete recording"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default CustomAudioPlayer

